import {disableNetwork, enableNetwork} from 'firebase/firestore';

import {db} from '../firebase';
import {analytics} from './analytics';

/**
 * Timeout + self-healing wrapper for one-shot Firestore reads (`getDoc` /
 * `getDocs`).
 *
 * Unlike a thrown error, a *stalled* Firestore stream produces no rejection: a
 * `getDocs` issued while the client's backend connection is half-open (after
 * sleep/wake, a network switch, or a long-backgrounded tab) can stay pending
 * forever. Every loader in the app follows `setLoading(true)` → `await read` →
 * `.finally(setLoading(false))`, so a read that never settles leaves the
 * spinner/skeleton up until the user manually reloads. This races the read
 * against a timer so the loader's `catch`/`finally` always runs, and — on
 * timeout — kicks the SDK to re-establish its connection so the *next* attempt
 * (a retry, a refresh, or simply navigating back in) succeeds without a full
 * page reload.
 *
 * This complements `firestoreRecovery.ts`, which handles the harder *fatal
 * assertion* crash by reloading the page; here we recover the far more common
 * silent stall in place.
 */

const DEFAULT_TIMEOUT_MS = 20_000;

export class FirestoreTimeoutError extends Error {
  label: string;
  timeoutMs: number;

  constructor(label: string, timeoutMs: number) {
    super(`Firestore read "${label}" timed out after ${timeoutMs}ms`);
    this.name = 'FirestoreTimeoutError';
    this.label = label;
    this.timeoutMs = timeoutMs;
  }
}

// Toggling the network drops *all* live listeners momentarily (they re-attach
// automatically), so don't do it more than once in a short window even if a
// burst of reads all time out together.
const BOUNCE_MIN_INTERVAL_MS = 30_000;
let lastBounceAt = 0;
let bounceInFlight: Promise<void> | null = null;

// A live capture session (scribe / interpreter / phone) streams its transcript
// through Firestore listeners. Those re-attach after a bounce, but to avoid any
// risk of a visible blip mid-visit we skip the active recovery on capture
// routes — the read still times out cleanly, we just don't toggle the network.
const CAPTURE_ROUTE_PREFIXES = [
  '/scribe',
  '/encounter',
  '/telephonic-encounter',
  '/group-encounter',
];

function onCaptureRoute(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location?.pathname || '';
  return CAPTURE_ROUTE_PREFIXES.some(
    prefix => path === prefix || path.startsWith(`${prefix}/`),
  );
}

/**
 * Force the Firestore client to tear down and rebuild its backend connection.
 * This is the SDK-sanctioned way to recover a wedged watch/write stream short
 * of reloading the page. Debounced and best-effort.
 */
export function recoverFirestoreConnection(): Promise<void> {
  if (bounceInFlight) return bounceInFlight;
  const now = Date.now();
  if (now - lastBounceAt < BOUNCE_MIN_INTERVAL_MS) return Promise.resolve();
  lastBounceAt = now;

  bounceInFlight = (async () => {
    try {
      await disableNetwork(db);
      await enableNetwork(db);
    } catch (err: any) {
      console.warn(
        '[firestoreTimeout] connection recovery failed:',
        err?.message || err,
      );
    } finally {
      bounceInFlight = null;
    }
  })();

  return bounceInFlight;
}

/**
 * Race a Firestore read against a timeout. On timeout, reject with a
 * `FirestoreTimeoutError` and (off capture routes) kick a connection recovery.
 *
 * @param promise  The in-flight `getDoc`/`getDocs` (or any read promise).
 * @param [opts.ms]     Timeout in ms (default 20s).
 * @param [opts.label]  Short identifier for telemetry/logging.
 */
export function withFirestoreTimeout<T>(
  promise: Promise<T>,
  {
    ms = DEFAULT_TIMEOUT_MS,
    label = 'firestore-read',
  }: {ms?: number; label?: string} = {},
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      try {
        analytics?.capture?.('firestore_read_timeout', {
          label,
          timeout_ms: ms,
          path:
            typeof window !== 'undefined'
              ? window.location?.pathname
              : undefined,
        });
      } catch {
        // Telemetry is best-effort; never let it mask the timeout.
      }
      if (!onCaptureRoute()) {
        void recoverFirestoreConnection();
      }
      reject(new FirestoreTimeoutError(label, ms));
    }, ms);
  });

  return Promise.race([promise, timeout]).finally(() => {
    if (timer) clearTimeout(timer);
  }) as Promise<T>;
}
